import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { PhoneCall, Mail, Loader2, Send, CornerDownRight, CheckCircle2, Inbox } from 'lucide-react';
import { CHANNEL_LABEL, toneStyle } from './returnsLoopTokens';

const API = '/api';
const fmt = (s) => { try { return s ? new Date(s).toLocaleString() : '—'; } catch { return s; } };

// datetime-local wants "YYYY-MM-DDTHH:mm" in LOCAL time
const localNow = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const inputCls = 'bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text';

/**
 * "Log an out-of-band contact" — a phone call, a text, a meeting, an email sent from
 * Randall's own mail client. Writes one outreach entry on the return and NOTHING else: no
 * campaign, no draft, no send. It also opens the 45-day reply window, so the person just
 * phoned can answer by email without the ingest dropping it.
 */
export function LogContact({ ret, actor, onSaved }) {
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState('phone');
  const [at, setAt] = useState(localNow());
  const [who, setWho] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/fitter-returns/${ret.return_id}/log-contact`, {
        channel, at: new Date(at).toISOString(), who: who || null, note: note || null, actor,
      });
      setOpen(false); setWho(''); setNote(''); setAt(localNow());
      onSaved?.(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Could not record the contact.'); }
    setBusy(false);
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="ret-log-contact-open"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text rounded-sm hover:border-hg-green">
        <PhoneCall size={12} /> Log an out-of-band contact
      </button>
    );
  }
  return (
    <div className="border rounded-sm p-3 space-y-2" style={toneStyle('outside')} data-testid="ret-log-contact">
      <div className="text-xs font-sans font-semibold">Record a contact that happened outside the Hub</div>
      <div className="flex items-center gap-2 flex-wrap">
        <select value={channel} onChange={(e) => setChannel(e.target.value)} className={inputCls}
          data-testid="ret-log-contact-channel">
          {['phone', 'email_direct', 'text', 'in_person'].map((c) => (
            <option key={c} value={c}>{CHANNEL_LABEL[c]}</option>
          ))}
        </select>
        <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} className={inputCls} />
        <input value={who} onChange={(e) => setWho(e.target.value)} placeholder="who (e.g. Doug, his wife)"
          className={`${inputCls} w-44`} />
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
        placeholder="what was said / agreed" className={`${inputCls} w-full`} data-testid="ret-log-contact-note" />
      {error && <div className="text-xs font-sans" style={toneStyle('error')}>{error}</div>}
      <div className="flex items-center gap-2">
        <button onClick={save} disabled={busy} data-testid="ret-log-contact-save"
          className="px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40">
          {busy ? <Loader2 size={12} className="animate-spin" /> : 'Record it'}
        </button>
        <button onClick={() => setOpen(false)} className="text-xs font-sans underline">cancel</button>
        <span className="text-[11px] font-sans">Sends nothing. It goes on the record and opens the reply window.</span>
      </div>
    </div>
  );
}

function ReplyBox({ ret, msg, actor, onSent, onCancel }) {
  const first = (ret.fitter_name || '').split(' ')[0] || 'there';
  const [subject, setSubject] = useState(
    (msg.subject || '').toLowerCase().startsWith('re:') ? msg.subject : `Re: ${msg.subject || ''}`);
  const [body, setBody] = useState(
    `${first},\n\n\n\nYour return page, for the address and the tracking box:\n${ret.helper_url || ''}\n\nRandall`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/fitter-returns/${ret.return_id}/reply`, {
        actor, subject, body, in_reply_to: msg.id, sender: 'randall',
      });
      onSent?.(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Send failed.'); }
    setBusy(false);
  };

  return (
    <div className="space-y-2 border-t border-hg-border pt-2" data-testid="ret-reply-box">
      <div className="text-[11px] font-sans px-2 py-1.5 border rounded-sm" style={toneStyle('warn')}>
        Replies go to <strong>{ret.fitter_email}</strong> as a one-recipient MailerLite campaign, from Randall.
        They are <strong>not threaded</strong> in {first}'s mail client and carry campaign tracking. The
        return link must stay in the body; the Hub refuses to send without it.
      </div>
      <input value={subject} onChange={(e) => setSubject(e.target.value)} className={`${inputCls} w-full`} />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8}
        className={`${inputCls} w-full leading-relaxed`} data-testid="ret-reply-body" />
      {error && <div className="text-xs font-sans px-2 py-1.5 border rounded-sm" style={toneStyle('error')}
        data-testid="ret-reply-error">{error}</div>}
      <div className="flex items-center gap-2">
        <button onClick={send} disabled={busy || !body.trim()} data-testid="ret-reply-send"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send reply
        </button>
        <button onClick={onCancel} className="text-xs font-sans underline">cancel</button>
      </div>
    </div>
  );
}

/**
 * Replies captured onto this return by the ingest — and the reply box. The conversation
 * lives on the return instead of in the general Customer Replies pile.
 */
export function ReturnThread({ ret, actor, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [sentNote, setSentNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/fitter-returns/${ret.return_id}/messages`);
      setData(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Could not load replies.'); }
    setLoading(false);
  }, [ret.return_id]);
  useEffect(() => { load(); }, [load]);

  const msgs = data?.messages || [];
  const unlinked = data?.unlinked_sends || [];
  const [linkError, setLinkError] = useState('');
  // An Outlook send the pickup could not tie to a message with certainty. The Hub does not
  // guess which message it answers — Randall does, with one click.
  const linkSend = async (mid, send) => {
    setLinkError('');
    try {
      const r = await axios.post(`${API}/fitter-returns/${ret.return_id}/link-reply`, {
        internet_message_id: send.internet_message_id, message_id: mid, actor,
      });
      setData(r.data);
      onChanged?.();
    } catch (e) { setLinkError(e.response?.data?.detail || 'Could not link that send.'); }
  };
  return (
    <div className="space-y-2" data-testid="ret-thread">
      {loading && <Loader2 size={14} className="animate-spin text-hg-green" />}
      {error && <div className="text-xs font-sans" style={toneStyle('error')}>{error}</div>}
      {sentNote && <div className="text-[11px] font-sans px-2 py-1.5 border rounded-sm flex items-center gap-1.5"
        style={toneStyle('ok')}><CheckCircle2 size={12} /> {sentNote}</div>}
      {!loading && msgs.length === 0 && (
        <div className="text-xs font-sans flex items-center gap-1.5" style={toneStyle('dim')}>
          <Inbox size={12} /> No reply from {ret.fitter_name?.split(' ')[0] || 'the holder'} yet
          {data?.addresses?.length ? <> — watching {data.addresses.join(', ')}</> : null}.
        </div>
      )}
      {msgs.map((m) => (
        <div key={m.id} className="border border-hg-border rounded-sm p-3 space-y-1.5 bg-hg-surface" data-testid="ret-thread-msg">
          <div className="flex items-center gap-2 flex-wrap text-[11px] font-sans">
            <Mail size={12} />
            <span style={toneStyle('neutral')} className="font-semibold">{m.from_name || m.from_email}</span>
            <span style={toneStyle('dim')}>{m.from_email} · {fmt(m.received_at)}</span>
            <span className="px-1.5 py-0.5 border rounded-sm" style={toneStyle(m.answered ? 'ok' : 'warn')}>
              {m.answered ? 'answered' : 'needs a reply'}
            </span>
          </div>
          <div className="text-xs font-sans font-semibold text-hg-text">{m.subject}</div>
          <div className="text-sm font-sans text-hg-text whitespace-pre-wrap border-l-2 border-hg-border pl-3">
            {m.body_text || '(no text)'}
          </div>
          {!m.answered && unlinked.filter((u) => String(u.at) >= String(m.received_at)).map((u) => (
            <button key={u.internet_message_id} onClick={() => linkSend(m.id, u)}
              data-testid="ret-link-send"
              className="block text-left text-[11px] font-sans px-2 py-1 border rounded-sm"
              style={toneStyle('outside')}>
              Your Outlook email of {fmt(u.at)} (“{u.subject}”) answers this — link it
            </button>
          ))}
          {replyTo === m.id ? (
            <ReplyBox ret={ret} msg={m} actor={actor} onCancel={() => setReplyTo(null)}
              onSent={(doc) => {
                setReplyTo(null);
                setSentNote(`Reply handed to MailerLite for ${doc?.fitter_email}. Delivery is confirmed a few minutes later.`);
                load(); onChanged?.(doc);
              }} />
          ) : (
            <button onClick={() => setReplyTo(m.id)} data-testid="ret-reply-open"
              className="inline-flex items-center gap-1 text-xs font-sans text-hg-text underline">
              <CornerDownRight size={12} /> Reply from the Hub
            </button>
          )}
        </div>
      ))}
      {linkError && <div className="text-xs font-sans" style={toneStyle('error')}>{linkError}</div>}
      {data?.coverage_note && (
        <p className="text-[10px] font-sans" style={toneStyle('dim')}>{data.coverage_note}</p>
      )}
    </div>
  );
}
